<template>
  <LayerStack :width="`${curSize}px`" :height="`${curSize}px`">
    <div v-if="isDebugMode">
      <v-btn @click="onDebugCameraPosition">Camera Position</v-btn>
    </div>
    <NewOneLayer v-if="!isInitialized">
      <LoadingLayer></LoadingLayer>
    </NewOneLayer>
    <OneLayer style="z-index: -9">
      <div id="background-canvas" class="layer__background" width="100%" height="100%" bottom center>
        <TheBackgroundCanvas ref="theBackgroundCanvas"></TheBackgroundCanvas>
      </div>
    </OneLayer>
    <!-- (구 backgroundLottiePlayer z-8 싱글턴 제거 — EFFECT cmd는 OVERLAY lottie/behind로 일괄 이관됨) -->
    <!-- 오버레이 밴드(모델 뒤): behind 레이어 오버레이 풀 + 편집기 정적 미리보기 -->
    <NewOneLayer class="ignore-event" style="z-index: -1">
      <OverlayItem
        v-for="o in overlaysBehind"
        :key="o.id"
        :overlay="o"
        :canvas-px="curSize"
        @done="removeOverlay"
      ></OverlayItem>
      <OverlayItem v-for="o in previewBehind" :key="o.id" :overlay="o" :canvas-px="curSize" frozen></OverlayItem>
      <!-- 파워 이펙트(파티클+콤보 미터) — 모델 뒤에서 발동. 채팅 "ㅋ" 트리거로 burstEffect() 호출. -->
      <power-mode
        v-if="effectEnabled"
        ref="fx"
        class="fx-layer"
        :preset="effectStyle"
        :shake="false"
        :combo-timeout="1500"
        :scale="1.4"
      ></power-mode>
    </NewOneLayer>
    <!--  -->
    <!-- cameraControl=false면 ignore-event로 마우스 이벤트를 통과 → OrbitControls 미동작(카메라 고정) -->
    <OneLayer class="model-layer" :class="{ 'ignore-event': !cameraControl }" style="z-index: 0">
      <div :id="id" center bottom></div>
    </OneLayer>
    <!--  -->
    <OneLayer class="ignore-event" style="z-index: 1">
      <div top right width="200px" height="200px"></div>
    </OneLayer>
    <OneLayer class="ignore-event" style="z-index: 2">
      <div top right :width="`${curSize}px`" :height="`${curSize}px`"></div>
    </OneLayer>
    <!-- (구 textAnime z2 / textCaption z3 / gifPlayer z2 싱글턴 제거 — 전부 OVERLAY(OverlayItem 밴드)로 일괄 이관됨) -->
    <!-- 오버레이 밴드(모델 앞): front 레이어 오버레이 풀 + 편집기 정적 미리보기 -->
    <NewOneLayer class="ignore-event" style="z-index: 5">
      <OverlayItem
        v-for="o in overlaysFront"
        :key="o.id"
        :overlay="o"
        :canvas-px="curSize"
        @done="removeOverlay"
      ></OverlayItem>
      <OverlayItem v-for="o in previewFront" :key="o.id" :overlay="o" :canvas-px="curSize" frozen></OverlayItem>
    </NewOneLayer>
    <AudioPlayer ref="audioPlayer"></AudioPlayer>
  </LayerStack>
</template>

<script>
import APP_CAMERA_POSITIONS from "@/constants/APP_CAMERA_POSITIONS";
import AppModelFacade from "@/domain/facades/AppModelFacade";
import AppRenderer from "@/domain/AppRenderer";

import { createThreeId } from "./utils/model-utils";

import delayFunc from "@/utils/delay";
import parseTextSchema from "./utils/parseTextSchema";
import { petNameTagText } from "./utils/petNameTag";
import { isLocal } from "@/utils/env-utils";

// NOTE: props로 modelIndex를 전달하는 경우
// http나 local storage로 가져올때 지연되서 가져오면 modelIndex가
// mounted 타이밍에는 이미 null이다. 이후에 변경하면 watch를 하던가 manual로 달아줘야 한다

export default {
  props: {
    size: {
      type: Number,
      default: 400,
    },
    // 실행 컨텍스트(paramter context가 우선이다)
    context: {
      type: Object,
      default: () => ({
        virtupetProfile: {},
      }),
    },
    // 편집기 정적 미리보기 오버레이 (재생헤드 시점). 각 항목이 OverlayItem의 overlay 프롭 형태.
    previewOverlays: {
      type: Array,
      default: () => [],
    },
    // 마우스 카메라 조작(OrbitControls). false면 모델 레이어가 이벤트를 통과시켜 카메라 고정.
    cameraControl: {
      type: Boolean,
      default: true,
    },
  },
  async created() {
    this.id = createThreeId();
  },
  async mounted() {
    try {
      if (this.modelIndex) this.init(this.modelIndex);
    } catch (error) {
      console.error(error);
    }
  },
  data() {
    return {
      isDebugMode: false && isLocal(),
      id: null,
      isInitialized: false,
      curSize: this.size, // 런타임 리사이즈용(상주 모델 페이지별 크기). size prop이 초기값.
      overlays: [], // 오버레이(씬) 풀. { id, overlayType, transform, source, textSchemas, nameTag, style, duration, layer }
      effectEnabled: false, // 파워 이펙트 on/off (제어판/콜드스타트로 설정)
      effectStyle: "particles", // 파워 이펙트 프리셋: particles|fireworks|flames|magic|heart
    };
  },
  computed: {
    overlaysBehind() {
      return this.overlays.filter((o) => o.layer === "behind");
    },
    overlaysFront() {
      return this.overlays.filter((o) => o.layer !== "behind");
    },
    previewBehind() {
      return this.previewOverlays.filter((o) => o.layer === "behind");
    },
    previewFront() {
      return this.previewOverlays.filter((o) => o.layer !== "behind");
    },
  },
  methods: {
    /**
     * PART1. LIFE CYCLES
     */
    // TODO: consider fallback tod default model
    async init(modelIndex, cameraPosition) {
      if (!modelIndex) throw new Error("E_MODEL_INDEX_IS_REQUIRED");
      if (this._appRenderer) throw new Error("E_MODEL_FACADE_ALREADY_INITIALIZED");

      try {
        this.cameraPosition = cameraPosition || APP_CAMERA_POSITIONS.ZOOM_OUT_1;
        const appModel = await AppModelFacade.fetchModel(modelIndex);

        // set size
        const parent = this.$el.querySelector(`#${this.id}`);
        const appRenderer = AppRenderer.create(
          appModel,
          { width: this.curSize, height: this.curSize },
          this.cameraPosition,
        );
        parent.appendChild(appRenderer.canvasEl);

        this._appRenderer = appRenderer;
        this.modelIndex = modelIndex;
        this.$emit("afterInit");

        this.isInitialized = true;
        return { animationClips: appModel.animations };
      } catch (error) {
        console.error(error);
      }

      //
    },
    // 런타임 리사이즈: 컨테이너(curSize) + 렌더러/카메라. 리로드 없이 선명하게.
    resize(size) {
      this.curSize = size;
      if (this._appRenderer) this._appRenderer.resize(size, size);
    },
    // 파워 이펙트 on/off + 프리셋 설정 (반응형 → v-if 마운트/언마운트, preset prop 갱신)
    setEffect(enabled, style) {
      this.effectEnabled = !!enabled;
      if (style) this.effectStyle = style;
    },
    // 파워 이펙트 1회 발동 — 모델 중앙 뒤에서 파티클 방사 (비활성/미마운트면 무시)
    burstEffect() {
      if (!this.effectEnabled || !this.$refs.fx) return;
      const c = this.curSize / 2;
      this.$refs.fx.trigger(c, c);
    },
    // 오디오 뮤트 토글 (AudioPlayer 전역 뮤트)
    setMuted(muted) {
      if (this.$refs.audioPlayer) this.$refs.audioPlayer.setMuted(muted);
    },
    async deinit() {
      try {
        const parent = this.$el.querySelector(`#${this.id}`);
        while (parent.firstChild) {
          parent.removeChild(parent.firstChild);
        }

        this._appRenderer = null;
      } catch (error) {
        console.warn("E_MODEL_FACADE_DEINIT_ERROR");
        console.warn(error);
      }
    },
    // 인생네컷: animationId의 t01(0~1) 지점 포즈를 캡처해 PNG dataURL 반환. (미초기화 시 null)
    capturePose(animationId, t01) {
      if (!this._appRenderer) return null;
      return this._appRenderer.poseAndCapture(animationId, t01);
    },
    async startIdle(instructionId = "") {
      if (!instructionId.includes("action-only")) {
        console.warn("WARNING: It is recommended that the InstructionId for the IDLE state starts with 'action-only'");
      }

      if (!instructionId.includes("idle")) {
        console.warn(
          "WARNING: It is recommended that the InstructionId for the IDLE state contain 'idle' (animation loop = infinite)",
        );
      }

      if (!this._appRenderer) return console.warn("E_CAN_NOT_START_IDLE : model facade not initialized");

      this.executeInstruction(instructionId);

      this._idleInstructionId = instructionId; // duration 컷 타이머의 idle 복귀에도 사용
      this._appRenderer.addFinishedListener((e) => {
        // 모션(비루프 애니)이 끝나면 "모델만" idle 애니로 복귀한다.
        //  예전엔 executeInstruction/playAction으로 복귀하면서 audioPlayer.stopAll()까지 호출 → 음악/오버레이/텍스트가
        //  모션 길이에 맞춰 잘렸다(옛 "모션+짧은 효과음" 모델). 지금은 리액션(씬)이 자체 duration을 가지므로,
        //  오디오·오버레이·텍스트는 각자 duration까지 유지되어야 한다(리액션 길이 존중). → 모델 애니만 재생.
        setTimeout(() => this._modelOnlyIdle(), 0);
      });
    },
    // 모델 애니만 idle로 복귀 (오디오/오버레이/텍스트 불변). 모션 자연 종료(finished)와 duration 컷 공용.
    _modelOnlyIdle() {
      const id = this._idleInstructionId;
      if (!id || !this._appRenderer) return;
      if (id.includes("action-only")) {
        const actionName = id.replace("action-only--", "");
        this._appRenderer.appAnimationController.play(`animation--${actionName}`);
      } else {
        this.executeInstruction(id);
      }
      this.showBackgroundCanvas();
    },
    // reaction 테이블 맵 주입 ({ byId, bySlug }). resolver가 id(rct_)·slug(system 하드코딩) 해석에 사용.
    setReactions({ byId = {}, bySlug = {} } = {}) {
      this._reactionById = byId;
      this._reactionBySlug = bySlug;
    },
    /**
     * PART2. External Control
     * NOTE: context 우선 순위 1. argument 2. props 3. {}
     */
    // 주석의 우선순위를 실제로 구현 — 인자가 비면 props context(펫 메타 등)로 폴백.
    //  (자막 이름표 등 컨텍스트 의존 렌더가 "컨텍스트 없이 실행되는 경로"에서도 동작하도록)
    _resolveContext(context) {
      if (context && Object.keys(context).length) return context;
      return this.context || {};
    },
    executeInstruction(instructionId, context = {}, userInput) {
      if (!instructionId) return console.warn("E_INSTRUCTION_ID_IS_REQUIRED : can not execute instruction without id");
      context = this._resolveContext(context);

      // @duplicate
      // this._appRenderer.appAnimationController.stop();
      // 새 리액션(씬) 시작 시 이전 오디오(배경음악·효과음)·오버레이 풀·모델 duration 컷 타이머 정리
      this.$refs.audioPlayer.stopAll();
      this.clearOverlays();
      clearTimeout(this._modelCutTimer);

      // action-only--idle, action-only--walk
      if (instructionId.includes("action-only")) {
        const actionName = instructionId.replace("action-only--", "");
        this.playAction(`animation--${actionName}`, context, userInput);
        return;
      }

      // reaction 테이블이 유일 소스: id(rct_) → slug(system 하드코딩 참조).
      const instruction =
        (this._reactionById && this._reactionById[instructionId]) ||
        (this._reactionBySlug && this._reactionBySlug[instructionId]) ||
        null;
      if (!instruction) return console.warn(`E_INSTRUCTION_NOT_FOUND: ${instructionId}`);

      const { cmds = [] } = instruction;
      this.executeCmds(cmds, context, userInput);
    },
    stopInstruction() {
      this._appRenderer.appAnimationController.stop();
      this.$refs.audioPlayer.stopAll();
      this.clearOverlays();
      clearTimeout(this._modelCutTimer);
    },
    // 일시정지/스크럽: 오디오·오버레이는 정리하되 모델은 "현재 포즈에서 동결" (stop=T-pose 방지).
    //  재개는 executeCmds가 resumeCurrent로 해제.
    pauseInstruction() {
      this.$refs.audioPlayer.stopAll();
      this.clearOverlays();
      clearTimeout(this._modelCutTimer);
      if (this._appRenderer) this._appRenderer.appAnimationController.pauseCurrent();
    },
    // 정지(리셋): 전체 정리 후 모델만 idle 복귀 (T-pose 방지, startIdle 재호출로 인한 finished 리스너 중복 없음)
    resetToIdle() {
      this.stopInstruction();
      this._modelOnlyIdle();
    },
    executeCmds(cmds = [], context = {}, userInput) {
      context = this._resolveContext(context); // 인자 비면 props(펫 메타) 폴백
      // 일시정지(포즈 동결) 상태에서 재개 시 동결 해제 (미동결이면 no-op)
      if (this._appRenderer) this._appRenderer.appAnimationController.resumeCurrent();
      cmds.forEach((cmd) => {
        this.executeOneCmd(cmd, context, userInput);
      });
    },
    // OVERLAY cmd → 오버레이 객체로 변환해 풀에 추가.
    // cmd: { _type:"OVERLAY", argSchema1:{ overlayType, source?, textSchemas?, nameTag? }, argSchema2:{ layer, transform, style, duration? }, duration? }
    _spawnOverlay(cmd, context = {}) {
      const a1 = cmd.argSchema1 || {};
      const a2 = cmd.argSchema2 || {};
      const overlayType = a1.overlayType;
      if (!overlayType) return console.warn("E_OVERLAY_TYPE_REQUIRED");

      // 텍스트/자막은 {{keyword}} 등 컨텍스트 치환
      const textSchemas = Array.isArray(a1.textSchemas)
        ? a1.textSchemas.map((s) => parseTextSchema(s, context))
        : undefined;
      // 자막 이름표 = 펫 메타(context.virtupetProfile)에서 자동 생성. 메타 없으면 숨김 (cmd 지정값 사용 안 함)
      let nameTag;
      if (overlayType === "caption") {
        const auto = petNameTagText(context && context.virtupetProfile);
        if (auto) nameTag = { text: auto };
      }

      // layer 기본값: lottie=behind, 그 외=front
      const layer = a2.layer || (overlayType === "lottie" ? "behind" : "front");

      this._overlaySeq = (this._overlaySeq || 0) + 1;
      const id = `ov_${this._overlaySeq}`;
      this.overlays.push({
        id,
        overlayType,
        layer,
        transform: a2.transform || null,
        style: a2.style || null,
        source: a1.source || null,
        textSchemas,
        nameTag,
        duration: cmd.duration || a2.duration || null,
      });
    },
    removeOverlay(id) {
      const i = this.overlays.findIndex((o) => o.id === id);
      if (i !== -1) this.overlays.splice(i, 1);
    },
    clearOverlays() {
      this.overlays = [];
    },
    async executeOneCmd(cmd, context, userInput) {
      try {
        const { targetRef, delay = 0 } = cmd;
        await delayFunc(delay);

        // 신규 오버레이 경로(씬 편집기): _type === "OVERLAY" → 오버레이 풀에 스폰(앞/뒤 밴드 N개 동시)
        if (cmd._type === "OVERLAY") {
          this._spawnOverlay(cmd, context);
          return;
        }

        if (targetRef === "model") {
          const { argSchema1: idTypeSchema } = cmd;

          const { actionId } = idTypeSchema;
          // duration 지정(씬 편집기): 클립 길이만큼 재생 — 자연 길이보다 길면 루프 반복, 시간이 되면 모델만 idle 복귀.
          //  미지정(레거시): LoopOnce + finished 리스너(기존 동작).
          clearTimeout(this._modelCutTimer);
          this._appRenderer.appAnimationController.play(actionId, { loop: !!cmd.duration });
          if (cmd.duration) {
            this._modelCutTimer = setTimeout(() => this._modelOnlyIdle(), cmd.duration);
          }
          // (레거시 분기 제거: textAnime/textCaption/gifPlayer/backgroundLottiePlayer —
          //  DB cmds가 OVERLAY로 일괄 이관됨. 롤백은 reaction_cmds_backup 참고.)
        } else if (this.isAudioCmd(cmd)) {
          this.$refs.audioPlayer.executeAudioCmd(cmd);
        } else {
          console.warn(`E_TARGET_REF_NOT_SUPPORTED: ${targetRef} `);
        }
      } catch (error) {
        console.error(error);
      }
    },
    stopSomething(targetRef) {
      if (targetRef === "model") {
        this._appRenderer.appAnimationController.stop();
      } else if (targetRef === "audioMusicPlayer") {
        this.$refs.audioPlayer.stopMusic();
      }
    },
    updateVisibility(targetRef, isVisible, userInput) {},
    changeBackgroundCanvas(fileName) {
      // TODO: json->lottie, gif -> play, img -> show
      // TODO: if filename is undefined, set to default (hide is default)
      if (!fileName) return this.$refs.theBackgroundCanvas.stop();
      this.$refs.theBackgroundCanvas.play(fileName, { scale: 1.8 });
    },
    changeModelReverse(bool) {
      if (bool) {
        this.$el.querySelector(".model-layer").style.transform = "scaleX(-1)";
      } else {
        this.$el.querySelector(".model-layer").style.transform = "scaleX(1)";
      }
    },
    //
    // 밖에서 호출할것 같지는 않은데 이쪽에 분류
    //
    showBackgroundCanvas() {
      this.$el.querySelector("#background-canvas").style.display = "block";
    },
    hideBackgroundCanvas() {
      this.$el.querySelector("#background-canvas").style.display = "none";
    },
    /**
     * PART3. LAYER LIFE CYCLE CONTROL
     */
    onCaptionEnd() {},
    /**
     * PART4. DEBUG
     */
    // (구 lottie 디버그 타겟은 backgroundLottiePlayer 싱글턴 제거와 함께 삭제됨)
    debug(debugTarget, ...args) {
      throw new Error("E_DEBUG_TARGET_NOT_SUPPORTED");
    },
    /**
     * UTILS
     */
    isAudioCmd(cmd) {
      const { _type, targetRef } = cmd;

      if (_type === "AUDIO") return true;
      if (targetRef.includes("audio")) return true;

      return false;
    },

    /**
     * WILL BE REFACTORED
     */
    playAction(actionId, context = {}, userInput) {
      // stop all.... (구 getBackgroundMeta 레거시 배경lottie는 싱글턴 제거와 함께 삭제 — animation--legacy-* 전용 죽은 경로였음)
      this.$refs.audioPlayer.stopAll();

      if (actionId && this._appRenderer) {
        this._appRenderer.appAnimationController.play(actionId);
      }
    },
    stopAction() {
      // TODO
      this._appRenderer.appAnimationController.stop();
    },

    onDebugCameraPosition() {
      this._appRenderer._debugCameraPosition();
    },
  },
};
</script>

<style scoped>
/* POSITION RESOLVER */
>>> .text-caption-wrapper {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 120px;
}

/* event 통과 */
.ignore-event {
  pointer-events: none;
}

/* 파워 이펙트 레이어 — behind 밴드(z:-1) 전체를 덮는 파티클 캔버스. 이벤트 통과. */
.fx-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
/* 콤보 미터: 기본은 박스 살짝 바깥으로 떠 있어(데모용) 방송 박스에선 넘침 → 안쪽으로.
   우/상단 앵커라 콤보 escalation(숫자 커짐)은 좌·하로 확장되어 최대 크기에서도 안쪽에 머문다. */
.fx-layer >>> .power-mode__combo {
  top: 10px;
  right: 12px;
}
</style>
